import { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Popover } from 'foundations-components/transitional/components/Popover';
import useCollaborationSidebarState from '../../hooks/useCollaborationSidebarState';
import EmbeddedCommentIframe from './components/EmbeddedCommentIframe';
import { DELETE_COMMENT_POPOVER_ID, MINI_COMMENTER_ID } from '../../constants/sidebar';
import useEmbeddedCommentsMessaging from './hooks/useEmbeddedCommentsMessaging';
import { useUserInfo } from '../../hooks/useUserInfo';
import useEmbeddedComments from './hooks/useEmbeddedComments';
import { useEmbeddedCommentPopover } from './hooks/useEmbeddedCommentPopover';
import { jsx as _jsx } from "react/jsx-runtime";
const PopoverContentOverlay = styled.div.withConfig({
  displayName: "EmbeddedCommentPopover__PopoverContentOverlay",
  componentId: "sc-25sd9o-0"
})([".hs-private.hs-private.hs-private &{pointer-events:none;position:fixed;top:", "px;left:", "px;width:", "px;height:", "px;}"], ({
  top
}) => top, ({
  left
}) => left, ({
  width
}) => width, ({
  height
}) => height);
export default function EmbeddedCommentPopover({
  iframeContainerID
}) {
  const {
    isEmbeddedPopoverOpen,
    embeddedPopoverPlacement,
    collaborationSidebarState,
    embeddedModuleID,
    embeddedMetaDescription,
    isEmbeddedOpenFromCommentMode,
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();
  const closePopoverOnOutsideClickRef = useRef(true);
  const embeddedPopoverRef = useRef(null);
  const embeddedPopoverContentRef = useRef(null);
  const popoverContentWrapperRef = useRef(null);
  const userInfo = useUserInfo();
  const closeEmbeddedCommentPopover = useCallback(() => {
    onUpdateCollaborationSidebarState({
      isEmbeddedPopoverOpen: false,
      commentableAreaClickCoordinates: undefined
    });
  }, [onUpdateCollaborationSidebarState]);
  const handleEmbeddedCommentOpenChange = useCallback(evt => {
    if (closePopoverOnOutsideClickRef.current && !document.querySelector(`[data-element-id="${DELETE_COMMENT_POPOVER_ID}"]`)) {
      if (!evt.target.value) {
        closeEmbeddedCommentPopover();
      }
    }
  }, [closeEmbeddedCommentPopover]);
  const {
    handleReady,
    broadcastDelete
  } = useEmbeddedCommentsMessaging({
    moduleId: embeddedModuleID,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    userInfo,
    isOpen: isEmbeddedPopoverOpen,
    onClose: closeEmbeddedCommentPopover
  });
  const {
    setModuleReply,
    removeModuleReply,
    broadcastCommentsMessage,
    moduleParentComment
  } = useEmbeddedComments({
    moduleId: embeddedModuleID,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    userInfo
  });
  const {
    dimensionsAndCoordinates
  } = useEmbeddedCommentPopover({
    iframeContainerID,
    popoverContentWrapperRef,
    closeEmbeddedCommentPopover
  });
  return /*#__PURE__*/_jsx(Popover, {
    arrowSize: 'none',
    autoPlacement: true,
    inlineSize: '380px',
    "data-test-id": "mini-commenter-popover",
    "data-onboarding": "mini-commenter-popover"
    // We need the embeddedModuleID as a key,
    // so that the popover is re-rendered and repositioned when
    // the embeddedModuleID changes
    ,

    open: isEmbeddedPopoverOpen,
    onOpenChange: handleEmbeddedCommentOpenChange,
    placement: embeddedPopoverPlacement,
    popoverRef: embeddedPopoverRef,
    closeOnOutsideClick: true,
    content: embeddedModuleID ? /*#__PURE__*/_jsx("div", {
      id: MINI_COMMENTER_ID,
      children: /*#__PURE__*/_jsx(EmbeddedCommentIframe, {
        contentWrapperRef: popoverContentWrapperRef,
        collaborationSidebarState: collaborationSidebarState,
        onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
        popoverRef: embeddedPopoverRef,
        autoFocus: isEmbeddedOpenFromCommentMode,
        moduleId: embeddedModuleID,
        handleConfirmDeleteCommentModalMessage: broadcastDelete,
        handleReady: handleReady,
        setModuleReply: setModuleReply,
        removeModuleReply: removeModuleReply,
        broadcastCommentsMessage: broadcastCommentsMessage,
        metaDescription: embeddedMetaDescription,
        moduleParentComment: moduleParentComment,
        onClose: closeEmbeddedCommentPopover,
        closePopoverOnOutsideClickRef: closePopoverOnOutsideClickRef,
        userInfo: userInfo
      })
    }) : null,
    children: /*#__PURE__*/_jsx(PopoverContentOverlay, {
      ref: embeddedPopoverContentRef,
      top: dimensionsAndCoordinates.top,
      left: dimensionsAndCoordinates.left,
      width: dimensionsAndCoordinates.width,
      height: dimensionsAndCoordinates.height
    })
  }, embeddedModuleID);
}